<template>
  <div class="collision-module" :class="{ 'collapsed': isCollapsed }">
    <div class="sci-fi-title" @click="toggleCollapse" :title="isCollapsed ? langMap.dashboard_expandModule[lang] : langMap.dashboard_collapseModule[lang]">
      <div class="title-bg"></div>
      <h3>
        <Icon type="ios-alert" /> {{ langMap.dashboard_collisionDetection[lang] }}
        <Icon :type="isCollapsed ? 'ios-arrow-down' : 'ios-arrow-up'" class="collapse-icon" />
      </h3>
      <div class="title-decoration"></div>
    </div>
    
    <div class="collision-container" v-show="!isCollapsed">
      <!-- 无数据提示 -->
      <div v-if="!collisionData || Object.keys(collisionData).length === 0" class="no-data-state">
        <div class="hologram-container">
          <div class="hologram-ring ring-1"></div>
          <div class="hologram-ring ring-2"></div>
          <div class="hologram-ring ring-3"></div>
          <div class="hologram-icon">
            <Icon type="ios-alert" />
          </div>
        </div>
        <div class="no-data-text">
          <div class="main-text">{{ langMap.dashboard_noData[lang] }}</div>
          <div class="sub-text">{{ langMap.dashboard_loading[lang] }}</div>
        </div>
      </div>
      
      <!-- 碰撞数据网格显示 -->
      <div v-else class="collision-content">
        <!-- 碰撞详细信息 - 网格布局 -->
        <div class="collision-grid">
          <div class="grid-item" v-for="field in displayData" :key="field.key">
            <div class="item-label">{{ field.label }}</div>
            <div class="item-value">{{ formatValue(field.value, field.needDivide) }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import collapsibleMixin from './collapsible-mixin';
import langMap from '@/utils/language';

export default {
  name: 'CollisionModule',
  mixins: [collapsibleMixin],
  props: {
    collisionData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      langMap,
    }
  },
  computed: {
    ...mapState({
      lang: state => state.app.language,
    }),
    hasCollision() {
      // 判断是否发生碰撞
      if (!this.collisionData || Object.keys(this.collisionData).length === 0) {
        return false;
      }
      // 根据实际数据结构判断，这里假设有collision字段
      return this.collisionData.collision === true || this.collisionData.status === 'collision';
    },
    displayData() {
      // 显示所有字段
      if (!this.collisionData) return [];
      
      const fields = [
        { key: 'time', label: this.langMap.dashboard_collisionTime[this.lang], value: this.collisionData.time, needDivide: false },
        { key: 'type', label: this.langMap.dashboard_collisionType[this.lang], value: this.collisionData.type, needDivide: false },
        { key: 'acceleration', label: `${this.langMap.dashboard_collisionAcceleration[this.lang]} (g)`, value: this.collisionData.acceleration, needDivide: false },
        { key: 'accelerationX', label: `${this.langMap.dashboard_collisionAccelerationX[this.lang]} (g)`, value: this.collisionData.accelerationX, needDivide: true },
        { key: 'accelerationY', label: `${this.langMap.dashboard_collisionAccelerationY[this.lang]} (g)`, value: this.collisionData.accelerationY, needDivide: true },
        { key: 'accelerationZ', label: `${this.langMap.dashboard_collisionAccelerationZ[this.lang]} (g)`, value: this.collisionData.accelerationZ, needDivide: true },
      ];
      
      // 只返回有值的字段（注意：0和'0'也是有效值）
      const result = fields.filter(field => {
        const val = field.value;
        return val !== undefined && val !== null && val !== '';
      });
      
      console.log('CollisionModule displayData:', result);
      return result;
    }
  },
  mounted() {
    console.log('CollisionModule mounted, collisionData:', this.collisionData);
    console.log('CollisionModule displayData:', this.displayData);
  },
  methods: {
    formatValue(value, needDivide = false) {
      if (value === null || value === undefined || value === '') {
        return '-';
      }
      
      // 如果是字符串且包含非数字字符（如日期时间），直接返回
      if (typeof value === 'string' && (value.includes('-') || value.includes(':') || value.includes(' '))) {
        return value;
      }
      
      // 如果是数字，保留2位小数
      const numValue = typeof value === 'number' ? value : parseFloat(value);
      if (!isNaN(numValue)) {
        // 如果需要除以10
        const finalValue = needDivide ? numValue / 10 : numValue;
        const decimalPart = (finalValue.toString().split('.')[1] || '');
        if (decimalPart.length > 2) {
          return finalValue.toFixed(2);
        }
        return finalValue;
      }
      
      return value;
    }
  }
}
</script>

<style lang="less" scoped>
.collision-module {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, rgba(0, 21, 41, 0.95), rgba(0, 10, 25, 0.95));
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5),
              inset 0 1px 0 rgba(0, 240, 255, 0.1);
  border: 1px solid rgba(0, 240, 255, 0.3);
  overflow: hidden;
  position: relative;
  transition: all 0.3s ease;
  min-height: 0;
  
  &.collapsed {
    flex: 0 0 auto;
  }
  
  .sci-fi-title {
    padding: 12px 16px;
    background: linear-gradient(135deg, rgba(0, 40, 80, 0.8), rgba(0, 21, 41, 0.9));
    border-bottom: 2px solid rgba(0, 240, 255, 0.5);
    position: relative;
    z-index: 10;
    cursor: pointer;
    user-select: none;
    transition: all 0.3s ease;
    
    &:hover {
      background: linear-gradient(135deg, rgba(0, 50, 90, 0.9), rgba(0, 30, 50, 0.95));
      border-bottom-color: rgba(0, 240, 255, 0.8);
    }
    
    h3 {
      margin: 0;
      font-size: 16px;
      font-weight: 700;
      color: #00f0ff;
      text-shadow: 0 0 15px rgba(0, 240, 255, 0.8);
      letter-spacing: 2px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      
      .collapse-icon {
        font-size: 18px;
        transition: transform 0.3s ease;
        margin-left: auto;
      }
    }
    
    .title-bg {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(90deg, transparent, rgba(0, 240, 255, 0.1), transparent);
      opacity: 0;
      transition: opacity 0.3s ease;
    }
    
    &:hover .title-bg {
      opacity: 1;
    }
    
    .title-decoration {
      position: absolute;
      bottom: -2px;
      left: 50%;
      transform: translateX(-50%);
      width: 60%;
      height: 2px;
      background: linear-gradient(90deg, transparent, #00f0ff, transparent);
      box-shadow: 0 0 10px rgba(0, 240, 255, 0.8);
    }
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, 
      transparent, 
      rgba(0, 240, 255, 1) 50%, 
      transparent
    );
    box-shadow: 0 0 20px rgba(0, 240, 255, 0.8);
    animation: scanline 2s linear infinite;
  }
  
  .collision-container {
    flex: 1;
    padding: 12px;
    overflow: hidden;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
  
  .collision-content {
    display: flex;
    flex-direction: column;
    height: 100%;
  }
  
  .collision-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
    flex: 1;
    align-content: start;
    
    .grid-item {
      background: linear-gradient(135deg, rgba(0, 240, 255, 0.08), rgba(0, 180, 255, 0.04));
      border: 1px solid rgba(0, 240, 255, 0.25);
      border-radius: 6px;
      padding: 8px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
      transition: all 0.3s ease;
      min-height: 60px;
      
      &:hover {
        background: linear-gradient(135deg, rgba(0, 240, 255, 0.15), rgba(0, 180, 255, 0.08));
        border-color: rgba(0, 240, 255, 0.4);
        transform: translateY(-2px);
      }
      
      .item-label {
        font-size: 10px;
        color: rgba(255, 255, 255, 0.6);
        margin-bottom: 4px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        width: 100%;
      }
      
      .item-value {
        font-size: 14px;
        font-weight: 700;
        color: #fff;
        font-family: 'Consolas', 'Monaco', monospace;
        text-shadow: 0 0 10px rgba(255, 255, 255, 0.4);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        width: 100%;
      }
    }
  }
  
  // 无数据状态
  .no-data-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 24px;
    padding: 48px 24px;
    min-height: 200px;
    
    .hologram-container {
      position: relative;
      width: 120px;
      height: 120px;
      display: flex;
      align-items: center;
      justify-content: center;
      
      .hologram-ring {
        position: absolute;
        border: 2px solid rgba(0, 240, 255, 0.4);
        border-radius: 50%;
        
        &.ring-1 {
          width: 120px;
          height: 120px;
          animation: pulse-ring 2s ease-in-out infinite;
        }
        
        &.ring-2 {
          width: 90px;
          height: 90px;
          animation: pulse-ring 2s ease-in-out 0.3s infinite;
        }
        
        &.ring-3 {
          width: 60px;
          height: 60px;
          animation: pulse-ring 2s ease-in-out 0.6s infinite;
        }
      }
      
      .hologram-icon {
        position: relative;
        z-index: 1;
        font-size: 48px;
        color: #00f0ff;
        text-shadow: 0 0 20px rgba(0, 240, 255, 0.8),
                     0 0 40px rgba(0, 240, 255, 0.6),
                     0 0 60px rgba(0, 240, 255, 0.4);
        animation: float 3s ease-in-out infinite;
        
        i {
          display: block;
        }
      }
    }
    
    .no-data-text {
      text-align: center;
      
      .main-text {
        font-size: 16px;
        font-weight: 600;
        color: #00f0ff;
        text-shadow: 0 0 10px rgba(0, 240, 255, 0.6);
        margin-bottom: 8px;
        letter-spacing: 1px;
      }
      
      .sub-text {
        font-size: 12px;
        color: rgba(255, 255, 255, 0.5);
        animation: pulse-text 2s ease-in-out infinite;
      }
    }
  }
}

@keyframes pulse-ring {
  0%, 100% {
    opacity: 0.4;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.05);
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

@keyframes pulse-text {
  0%, 100% {
    opacity: 0.5;
  }
  50% {
    opacity: 1;
  }
}

@keyframes scanline {
  0% {
    transform: translateY(0);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    transform: translateY(500px);
    opacity: 0;
  }
}
</style>
